import React, { memo } from 'react';
import classNames from 'classnames';
import GainSettingsProps from '../GainSettingsProps';
import { getColumnGroups, getRowGroups, renderCell, renderHeader, isNetworkElementSupported } from './utils';
import styles from './GainSettingsTable.module.css';

const TABLE_ID = 'gs';

const GainSettingsTable = memo(function GainSettingsTable({ su1 = false, su2 = false, su3 = false, su4 = false }) {
  const columns = [su1, su2, su3, su4].reduce(
    (acc, available, index) => {
      if (available) {
        acc.push(createDeviceColumnGroup(`SU${index + 1}`));
      }

      return acc;
    },
    [
      {
        id: 'metric',
        header: '',
        headerClassName: styles.rowHeading,
        columns: [
          {
            id: 'metric.name',
            header: 'Gain Settings',
            headerClassName: classNames(styles.rowHeading, styles.gainSettingsMainHeader),
            cell: (row) => <GainSettingsProps.Title>{row.name}</GainSettingsProps.Title>,
            cellClassName: styles.rowHeading
          }
        ]
      },
      createDeviceColumnGroup('DU')
    ]
  );
  const rows = [
    {
      id: 'agc-gain-caps-rx-hpol-db',
      group: 'agc-gain-caps',
      name: 'AGC Gain Caps RX HPol (dB)',
      su: true
    },
    {
      id: 'agc-gain-caps-rx-vpol-db',
      group: 'agc-gain-caps',
      name: 'AGC Gain Caps RX VPol (dB)',
      su: true
    },
    {
      id: 'agc-gain-caps-tx-hpol-db',
      group: 'agc-gain-caps',
      name: 'AGC Gain Caps TX HPol (dB)',
      su: true
    },
    {
      id: 'agc-gain-caps-tx-vpol-db',
      group: 'agc-gain-caps',
      name: 'AGC Gain Caps TX VPol (dB)',
      su: true
    }
  ];
  const columnGroups = getColumnGroups(columns);
  const rowGroups = getRowGroups(rows);

  const cableLossColumns = [
    {
      id: 'metric',
      header: '',
      headerClassName: styles.rowHeading,
      columns: [
        {
          id: 'metric.name',
          header: (
            <>
              Cable Loss <span className={styles.rangeText}>(0-30dB, 0.5dB steps)</span>
            </>
          ),
          headerClassName: classNames(styles.rowHeading, styles.gainSettingsMainHeader),
          cell: (row) => (
            <div className={styles.suInfoContainer}>
              <GainSettingsProps.Title>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div>
                    <GainSettingsProps.Id networkElement={row.networkElement} />
                  </div>
                  <div>({row.name})</div>
                </div>
              </GainSettingsProps.Title>
              {/* <span>({row.range})</span> */}
            </div>
          ),
          cellClassName: styles.rowHeading
        }
      ]
    },
    {
      id: 'values',
      header: '',
      columns: [
        {
          id: 'current',
          header: 'Current',
          headerClassName: styles.columnHeading,
          cell: (row) => (
            <GainSettingsProps.Value
              style={{ height: '100%', display: 'flex', alignItems: 'center' }}
              name={`DU.gain-settings.${row.id}`}
              aria-labelledby={`${getId('cable-loss')} ${getId('current')} ${getId(row.id)}`}
            />
          )
        },
        {
          id: 'new',
          header: 'New',
          headerClassName: styles.columnHeading,
          cell: (row) => (
            <GainSettingsProps.Input
              name={`DU.gain-settings.${row.id}`}
              aria-labelledby={`${getId('cable-loss')} ${getId('new')} ${getId(row.id)}`}
              min={0}
              max={30}
              step={0.5}
            />
          )
        }
      ]
    }
  ];

  const cableLossRows = [
    {
      id: 'default-cable-loss-rf-path1-db',
      group: 'default-cable-loss',
      name: 'Sector 1',
      networkElement: 'SU1',
      range: '0-30dB, 0.5dB steps'
    },
    {
      id: 'default-cable-loss-rf-path2-db',
      group: 'default-cable-loss',
      name: 'Sector 2',
      networkElement: 'SU2',
      range: '0-30dB, 0.5dB steps'
    },
    {
      id: 'default-cable-loss-rf-path3-db',
      group: 'default-cable-loss',
      name: 'Sector 3',
      networkElement: 'SU3',
      range: '0-30dB, 0.5dB steps'
    }
  ];

  const cableLossColumnGroups = getColumnGroups(cableLossColumns);
  const cableLossRowGroups = getRowGroups(cableLossRows);

  return (
    <>
      <div role="table" className={styles.root}>
        <div role="rowgroup" className={styles.header}>
          {columnGroups.map((columnGroup) => (
            <div key={columnGroup.id} role="row" className={styles.row}>
              {columnGroup.columns.map((column) => (
                <div
                  key={column.id}
                  id={getId(column.id)}
                  role="columnheader"
                  className={classNames(styles.heading, [column.headerClassName])}
                >
                  {renderHeader(column)}
                </div>
              ))}
            </div>
          ))}
        </div>
        {rowGroups.map((rowGroup) => (
          <div key={rowGroup.id} role="rowgroup" className={styles.group}>
            {rowGroup.rows.map((row) => (
              <div key={row.id} role="row" className={styles.row}>
                {columnGroups[columnGroups.length - 1].columns.map((column, index) =>
                  index === 0 ? (
                    <div
                      key={column.id}
                      id={getId(row.id)}
                      role="rowheader"
                      className={classNames(styles.heading, [column.cellClassName])}
                    >
                      {renderCell(row, column)}
                    </div>
                  ) : (
                    <div key={column.id} role="cell" className={classNames(styles.cell, [column.cellClassName])}>
                      {renderCell(row, column)}
                    </div>
                  )
                )}
              </div>
            ))}
          </div>
        ))}
      </div>

      <div role="table" className={classNames(styles.root, styles.cableLossTable)}>
        <div role="rowgroup" className={styles.header}>
          {cableLossColumnGroups.map((columnGroup) => (
            <div key={columnGroup.id} role="row" className={styles.row}>
              {columnGroup.columns.map((column) => (
                <div
                  key={column.id}
                  id={getId(column.id)}
                  role="columnheader"
                  className={classNames(styles.heading, [column.headerClassName])}>
                  {renderHeader(column)}
                </div>
              ))}
            </div>
          ))}
        </div>
        {cableLossRowGroups.map((rowGroup) => (
          <div key={`cable-rowgroup-${rowGroup.id}`} role="rowgroup" className={styles.group}>
            {rowGroup.rows
              .slice(0, columnGroups[0].columns.length - 2) // Only show rows based on columnGroups length
              .map((row) => (
                <div key={`cable-row-${rowGroup.id}-${row.id}`} role="row" className={styles.row}>
                  {cableLossColumnGroups[cableLossColumnGroups.length - 1].columns.map((column) => (
                    <div
                      key={`cable-cell-${row.id}-${column.id}`}
                      role={column.id === 'metric.name' ? 'rowheader' : 'cell'}
                      className={classNames(column.id === 'metric.name' ? styles.heading : styles.cell, [
                        column.cellClassName
                      ])}
                      id={column.id === 'metric.name' ? getId(row.id) : undefined}
                    >
                      {renderCell(row, column)}
                    </div>
                  ))}
                </div>
              ))}
          </div>
        ))}
      </div>
    </>
  );
});

export default GainSettingsTable;

function createDeviceColumnGroup(networkElement) {
  return {
    id: networkElement,
    header: () => <GainSettingsProps.Id networkElement={networkElement} />,
    headerClassName: styles.columnGroup,
    columns: [
      {
        id: `${networkElement}.value`,
        header: 'Current',
        headerClassName: styles.columnHeading,
        cell: (row) =>
          isNetworkElementSupported(row, networkElement) ? (
            <GainSettingsProps.Value
              name={`${networkElement}.gain-settings.${row.id}`}
              aria-labelledby={`${getId(networkElement)} ${getId(`${networkElement}.value`)} ${getId(row.id)}`}
            />
          ) : null
      },
      {
        id: `${networkElement}.input`,
        header: 'New',
        headerClassName: styles.columnHeading,
        cell: (row) =>
          isNetworkElementSupported(row, networkElement) ? (
            <GainSettingsProps.Input
              name={`${networkElement}.gain-settings.${row.id}`}
              aria-labelledby={`${getId(networkElement)} ${getId(`${networkElement}.input`)} ${getId(row.id)}`}
            />
          ) : null
      }
    ]
  };
}

function getId(id) {
  return `${TABLE_ID}.${id}`;
}
